// Sugestão de categoria orientada por regras cadastradas em Configurações → Divisões de Categorias.

const norm = (s) => (s || "").toUpperCase().replace(/[’´`]/g, "'").replace(/\s+/g, " ").trim();
const fmtH = (cm) => `${(cm / 100).toFixed(2).replace(".", ",")} m`;
const fmtW = (kg) => `${String(kg).replace(".", ",")} kg`;

export const ageAt = (nascimento, ref) => {
  if (!nascimento) return null;
  const b = new Date(`${nascimento}T00:00:00`);
  const r = ref ? new Date(`${String(ref).slice(0, 10)}T00:00:00`) : new Date();
  if (isNaN(b) || isNaN(r)) return null;
  let a = r.getFullYear() - b.getFullYear();
  if (r.getMonth() < b.getMonth() || (r.getMonth() === b.getMonth() && r.getDate() < b.getDate())) a -= 1;
  return a;
};

const FEM_KEYS = ["BIKINI", "BIQUINI", "WELLNESS", "WOMEN", "FIGURE", "FIT MODEL", "FEMININ"];
const MASC_KEYS = ["MEN'S", "MENS", "BODYBUILDING", "CLASSIC", "MASCULIN", "CULTURISMO"];
export const inferSexo = (nome) => {
  const u = norm(nome);
  if (FEM_KEYS.some((k) => u.includes(k))) return "F";
  if (MASC_KEYS.some((k) => u.includes(k))) return "M";
  return null;
};

const classLetter = (nome) => {
  const u = norm(nome).replace(/MASTERS?\s+[A-G]\b/g, "MASTERS");
  const m = u.match(/\b(?:CLASSE|CLASS|CAT\.?|CATEGORIA)\s+([A-G])\b/) || u.match(/\b([A-G])\b(?=\s*(?:—|-|–|$|\(|ATÉ|ACIMA|\d))/);
  return m ? m[1] : null;
};

const numLimits = (nome) => {
  const u = norm(nome).replace(/,/g, ".");
  const out = {};
  const wMax = u.match(/AT[ÉE]\s*(\d{2,3})\s*KG/);
  const wMin = u.match(/ACIMA\s*(?:DE)?\s*(\d{2,3})\s*KG/);
  const hMax = u.match(/AT[ÉE]\s*(1\.\d{2}|2\.\d{2})\s*M?/);
  const hMin = u.match(/ACIMA\s*(?:DE)?\s*(1\.\d{2}|2\.\d{2})\s*M?/);
  if (wMax) out.wMax = parseFloat(wMax[1]);
  if (wMin) out.wMin = parseFloat(wMin[1]);
  if (hMax) out.hMax = Math.round(parseFloat(hMax[1]) * 100);
  if (hMin) out.hMin = Math.round(parseFloat(hMin[1]) * 100);
  return out;
};

const inRange = (v, min, max) => (min == null || v > min) && (max == null || v <= max);
const rangeTxt = (min, max, fmt) => (min != null && max != null ? `${fmt(min)}–${fmt(max)}` : max != null ? `até ${fmt(max)}` : `acima de ${fmt(min)}`);

// Avalia uma regra contra o perfil. Retorna { ok, motivo, nivel } ou null quando faltam dados obrigatórios.
const evalRule = (r, { sexo, alturaCm, pesoKg, idade }) => {
  if (r.sexo !== "ambos" && sexo && sexo !== r.sexo) return { ok: false };
  if (r.sexo !== "ambos" && !sexo) return null;
  const reasons = [];
  let nivel = "forte";
  const hasW = r.peso_min != null || r.peso_max != null;
  const hasH = r.altura_min_cm != null || r.altura_max_cm != null;
  const hasA = r.idade_min != null || r.idade_max != null;
  if (hasW) {
    if (pesoKg == null) return null;
    if (!inRange(pesoKg, r.peso_min, r.peso_max)) return { ok: false };
    reasons.push(`Peso ${fmtW(pesoKg)} (${rangeTxt(r.peso_min, r.peso_max, fmtW)})`);
  }
  if (hasH) {
    if (alturaCm == null) return null;
    if (!inRange(alturaCm, r.altura_min_cm, r.altura_max_cm)) return { ok: false };
    reasons.push(`Altura ${fmtH(alturaCm)} (${rangeTxt(r.altura_min_cm, r.altura_max_cm, fmtH)})`);
  }
  if (hasA) {
    if (idade == null) return null;
    const okA = (r.idade_min == null || idade >= r.idade_min) && (r.idade_max == null || idade <= r.idade_max);
    if (!okA) return { ok: false };
    reasons.push(`Idade ${idade}${r.idade_min != null ? ` (${r.idade_min}+)` : ` (até ${r.idade_max})`}`);
  }
  if (r.experiencia === "true_novice") { reasons.push("Se for a primeira competição"); nivel = "possivel"; }
  if (r.experiencia === "novice") { reasons.push("Se já competiu mas nunca venceu"); nivel = "possivel"; }
  if (!hasW && !hasH && !hasA && !r.experiencia) { reasons.push(r.nome); nivel = "possivel"; }
  return { ok: true, motivo: `${r.nome}: ${reasons.join(" · ")}`, nivel };
};

/**
 * suggestCategories(profile, cats, eventDate, rules) → [{ cat, motivo, nivel }]
 * Uma categoria é sugerida quando TODAS as regras ativas que casam com o nome dela aceitam o perfil.
 */
export const suggestCategories = ({ sexo, alturaCm, pesoKg, nascimento }, cats, eventDate, rules = []) => {
  if (!sexo && !alturaCm && !pesoKg && !nascimento) return [];
  const idade = ageAt(nascimento, eventDate);
  const active = rules.filter((r) => r.ativo !== false && (r.divisao || r.palavra_chave));
  const out = [];
  for (const cat of cats) {
    const u = norm(cat.nome);
    const letter = classLetter(cat.nome);
    const catSexo = inferSexo(cat.nome);
    if (catSexo && sexo && catSexo !== sexo) continue;
    if (catSexo && !sexo) continue;
    let matched = active.filter((r) =>
      (!r.divisao || u.includes(norm(r.divisao))) &&
      (!r.classe || letter === r.classe) &&
      (!r.palavra_chave || u.includes(norm(r.palavra_chave)))
    );
    // "NOVICE" não deve disparar junto com "TRUE NOVICE": fica só a palavra-chave mais específica
    matched = matched.filter((r) => !matched.some((o) =>
      o !== r && r.palavra_chave && o.palavra_chave && o.palavra_chave.length > r.palavra_chave.length &&
      norm(o.palavra_chave).includes(norm(r.palavra_chave))
    ));
    if (!matched.length) continue;
    const lim = numLimits(cat.nome);
    const reasons = [];
    let nivel = "forte";
    let ok = true;
    if (lim.wMax != null && (pesoKg == null || pesoKg > lim.wMax)) ok = false;
    if (lim.wMin != null && (pesoKg == null || pesoKg <= lim.wMin)) ok = false;
    if (lim.hMax != null && (alturaCm == null || alturaCm > lim.hMax)) ok = false;
    if (lim.hMin != null && (alturaCm == null || alturaCm <= lim.hMin)) ok = false;
    if (!ok) continue;
    if (lim.wMax != null || lim.wMin != null) reasons.push(`Peso ${fmtW(pesoKg)} dentro da faixa do nome`);
    if (lim.hMax != null || lim.hMin != null) reasons.push(`Altura ${fmtH(alturaCm)} dentro da faixa do nome`);
    const hasNameLimits = Object.keys(lim).length > 0;
    for (const r of matched) {
      // limites explícitos no nome prevalecem sobre peso/altura da regra da mesma divisão
      const stripped = hasNameLimits && r.divisao;
      const rr = stripped ? { ...r, peso_min: null, peso_max: null, altura_min_cm: null, altura_max_cm: null } : r;
      const res = evalRule(rr, { sexo, alturaCm, pesoKg, idade });
      if (res === null) { ok = false; break; }
      if (!res.ok) { ok = false; break; }
      if (stripped && r.idade_min == null && r.idade_max == null && !r.experiencia) { reasons.push(r.nome); continue; }
      if (res.nivel === "possivel") nivel = "possivel";
      reasons.push(res.motivo);
    }
    if (!ok) continue;
    out.push({ cat, motivo: reasons.join(" · "), nivel });
  }
  return out.sort((a, b) => (a.nivel === b.nivel ? (a.cat.ordem ?? 0) - (b.cat.ordem ?? 0) : a.nivel === "forte" ? -1 : 1));
};
